import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { X } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import type { FormDialogProps } from './types';
import './styles';

interface FormDialogApi {
  /** Закрыть так же, как Escape: с вопросом, если есть несохранённый ввод. */
  requestClose: () => void;
}

const FormDialogContext = createContext<FormDialogApi | null>(null);

/**
 * Для кнопки «Отмена» внутри `FormDialog`: она должна закрывать ТАК ЖЕ, как
 * Escape и крестик — с вопросом при несохранённом вводе. Вне окна — пустая
 * функция.
 */
export function useFormDialogClose(): () => void {
  return useContext(FormDialogContext)?.requestClose ?? (() => undefined);
}

/**
 * Окно с формой. Правила (docs: «Основа форм», п. 4):
 *
 * 1. Ширина 480 / 640 / 880 по `size`; высота не больше экрана минус 32 px;
 *    шапка и низ не прокручиваются, прокручивается тело.
 * 2. Уже 600 px — лист во весь экран, низ прижат к краю с учётом выреза.
 * 3. Escape, крестик и клик по фону — это ЗАПРОС на закрытие: при
 *    `isDirty` сначала «Закрыть без сохранения?».
 * 4. Ошибка сервера (`error`) — блок над кнопками, фокус на нём.
 *
 * Построено на RAC `Modal`: фокус заперт в окне, возвращается на открывший
 * элемент, фон недоступен диктору. `Dialog` (подтверждение) остаётся для
 * коротких вопросов без формы.
 */
export function FormDialog({ title, subtitle, size = 'md', isOpen, onClose, children, footer, footerNote, isDismissable = true, isDirty = false, error }: FormDialogProps) {
  const t = useT();
  const [asking, setAsking] = useState(false);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) setAsking(false);
  }, [isOpen]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const api = useMemo<FormDialogApi>(
    () => ({
      requestClose: () => {
        if (isDirty) setAsking(true);
        else onClose();
      },
    }),
    [isDirty, onClose],
  );

  return (
    <FormDialogContext.Provider value={api}>
      <ModalOverlay
        className="fm-overlay"
        isOpen={isOpen}
        isDismissable={isDismissable}
        onOpenChange={(open) => {
          if (!open) api.requestClose();
        }}
      >
        <Modal className="fm-modal" data-size={size}>
          <Dialog className="fm-dialog">
            <header>
              <div className="fm-dialog__titles">
                {/* Длинное название — до двух строк и многоточие; целиком — в `title`. */}
                <Heading slot="title" className="fm-dialog__title" title={title}>
                  {title}
                </Heading>
                {subtitle ? <p className="fm-dialog__subtitle">{subtitle}</p> : null}
              </div>
              <Button className="fm-dialog__close" aria-label={t(FM.dialogClose)} onPress={api.requestClose}>
                <X size={20} aria-hidden />
              </Button>
            </header>
            <div className="fm-dialog__body">{children}</div>
            <div className="fm-dialog__foot">
              {error ? (
                <div ref={errorRef} className="fm-dialog__error" role="alert" tabIndex={-1}>
                  {error}
                </div>
              ) : null}
              {footerNote ? <div className="fm-actions__note">{footerNote}</div> : null}
              {footer}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <ModalOverlay className="fm-overlay fm-confirm-overlay" isOpen={isOpen && asking} isDismissable onOpenChange={(open) => !open && setAsking(false)}>
        <Modal className="fm-confirm">
          <Dialog role="alertdialog" className="fm-confirm-dialog">
            <Heading slot="title" className="fm-confirm__title">
              {t(FM.discardTitle)}
            </Heading>
            <p>{t(FM.discardBody)}</p>
            <div className="fm-actions">
              <div className="fm-actions__buttons">
                <Button autoFocus className="btn btn--ghost focus-ring" onPress={() => setAsking(false)}>
                  {t(FM.discardKeep)}
                </Button>
                <Button
                  className="btn btn--secondary focus-ring"
                  onPress={() => {
                    setAsking(false);
                    onClose();
                  }}
                >
                  {t(FM.discardConfirm)}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </FormDialogContext.Provider>
  );
}
